import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface RadarData {
    label: string;
    value: number;
}

@Component({
    selector: 'app-radar-chart',
    standalone: true,
    imports: [CommonModule],
    template: `<div #chartContainer class="radar-chart-container w-full h-full relative"></div>`,
    styles: [`
    .radar-chart-container {
      min-height: 300px;
    }
    .radar-tooltip {
      display: none;
      opacity: 0;
      position: absolute;
      background: #232327;
      color: white;
      padding: 8px 12px;
      border-radius: 8px;
      font-size: 12px;
      pointer-events: none;
      z-index: 100;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    }
  `],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class RadarChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
    @Input() data: RadarData[] = [];
    @Input() levels: number = 4;
    @Input() maxValue: number = 0;
    @Input() color: string = '#ad46ff';

    private themeService = inject(ThemeService);
    private resizeObserver!: ResizeObserver;
    private themeSubscription?: Subscription;
    private currentTheme: 'light' | 'dark' = 'dark';

    ngOnInit() {
        this.currentTheme = this.themeService.getTheme();
    }

    ngAfterViewInit() {
        this.themeSubscription = this.themeService.theme$.subscribe(theme => {
            this.currentTheme = theme;
            this.drawChart();
        });

        this.resizeObserver = new ResizeObserver(() => {
            this.drawChart();
        });
        this.resizeObserver.observe(this.chartContainer.nativeElement);
        this.drawChart();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] && !changes['data'].firstChange) {
            this.drawChart();
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.themeSubscription) this.themeSubscription.unsubscribe();
    }

    private drawChart() {
        if (!this.data || this.data.length < 3) return;

        const element = this.chartContainer.nativeElement;
        d3.select(element).selectAll('*').remove();

        const width = element.clientWidth;
        const height = element.clientHeight || 300;
        const margin = { top: 40, right: 80, bottom: 40, left: 80 };
        const radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) * 0.45;

        const svg = d3.select(element)
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', `0 0 ${width} ${height}`)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${height / 2})`);

        const maxVal = this.maxValue || d3.max(this.data, d => d.value) || 100;
        const total = this.data.length;
        const angleStep = (Math.PI * 2) / total;

        // Tooltip
        const tooltip = d3.select(element)
            .append('div')
            .attr('class', 'radar-tooltip');

        const themeColors = this.getThemeColors();

        // Draw Grid Levels
        for (let i = 0; i < this.levels; i++) {
            const levelRadius = (radius / this.levels) * (i + 1);
            const levelPoints: [number, number][] = [];

            for (let j = 0; j <= total; j++) {
                const angle = j * angleStep - Math.PI / 2;
                levelPoints.push([
                    levelRadius * Math.cos(angle),
                    levelRadius * Math.sin(angle)
                ]);
            }

            svg.append('path')
                .datum(levelPoints)
                .attr('d', d3.line())
                .attr('fill', 'none')
                .attr('stroke', themeColors.grid)
                .attr('stroke-width', 1)
                .attr('opacity', 0.5);

            // Grid Labels (Values)
            svg.append('text')
                .attr('x', 5)
                .attr('y', -levelRadius)
                .attr('fill', themeColors.labels)
                .style('font-size', '10px')
                .text(Math.round((maxVal / this.levels) * (i + 1)));
        }

        // Draw Axes
        const axes = svg.selectAll('.axis')
            .data(this.data)
            .enter()
            .append('g')
            .attr('class', 'axis');

        axes.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', (d, i) => radius * Math.cos(i * angleStep - Math.PI / 2))
            .attr('y2', (d, i) => radius * Math.sin(i * angleStep - Math.PI / 2))
            .attr('stroke', themeColors.grid)
            .attr('stroke-width', 1)
            .attr('opacity', 0.5);

        // Axis Labels
        axes.append('text')
            .attr('x', (d, i) => (radius + 20) * Math.cos(i * angleStep - Math.PI / 2))
            .attr('y', (d, i) => (radius + 20) * Math.sin(i * angleStep - Math.PI / 2))
            .attr('fill', themeColors.labels)
            .attr('text-anchor', (d, i) => {
                const angle = i * angleStep - Math.PI / 2;
                if (Math.abs(Math.cos(angle)) < 0.1) return 'middle';
                return Math.cos(angle) > 0 ? 'start' : 'end';
            })
            .attr('dy', '0.35em')
            .style('font-size', width < 350 ? '10px' : '12px')
            .text(d => d.label);

        // Draw Shape
        const radarLine = d3.lineRadial<RadarData>()
            .radius(d => (d.value / maxVal) * radius)
            .angle((d, i) => i * angleStep)
            .curve(d3.curveLinearClosed);

        svg.append('path')
            .datum(this.data)
            .attr('d', radarLine)
            .attr('fill', this.color)
            .attr('fill-opacity', 0.6)
            .attr('stroke', this.color)
            .attr('stroke-width', 2);

        // Draw Dots
        svg.selectAll('.dot')
            .data(this.data)
            .enter()
            .append('circle')
            .attr('class', 'dot')
            .attr('cx', (d, i) => (d.value / maxVal) * radius * Math.cos(i * angleStep - Math.PI / 2))
            .attr('cy', (d, i) => (d.value / maxVal) * radius * Math.sin(i * angleStep - Math.PI / 2))
            .attr('r', 4)
            .attr('fill', this.color)
            .style('cursor', 'pointer')
            .on('mouseover', (event, d) => {
                tooltip.style('display', 'block')
                    .style('opacity', 1)
                    .html(`<strong>${d.label}</strong><br>Click: ${d.value}`)
                    .style('left', (event.offsetX + 10) + 'px')
                    .style('top', (event.offsetY - 10) + 'px')
                    .style('background', themeColors.tooltipBg)
                    .style('color', themeColors.tooltipText)
                    .style('border', themeColors.tooltipBorder);

                d3.select(event.currentTarget)
                    .transition().duration(200)
                    .attr('r', 6);
            })
            .on('mousemove', (event) => {
                tooltip.style('left', (event.offsetX + 10) + 'px')
                    .style('top', (event.offsetY - 10) + 'px');
            })
            .on('mouseout', (event) => {
                tooltip.style('opacity', 0)
                    .style('display', 'none');
                d3.select(event.currentTarget)
                    .transition().duration(200)
                    .attr('r', 4);
            });
    }

    private getThemeColors() {
        const isDark = this.currentTheme === 'dark';
        return {
            grid: isDark ? '#ffffff33' : '#B8B8E0',
            labels: isDark ? '#A1A1AA' : '#64748B',
            tooltipBg: isDark ? '#232327' : '#ffffff',
            tooltipText: isDark ? '#ffffff' : '#141437',
            tooltipBorder: isDark ? 'none' : '1px solid #E2E8F0',
            shadow: isDark ? '0px 4px 12px rgba(0,0,0,0.5)' : '0px 4px 12px rgba(0,0,0,0.1)'
        };
    }
}
